import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { PAYMENT_STATUS_NAMES } from "../../../../components/cells/OrderPaymentStatusCellV2";
import { getRefundReasonLabel } from "./refundReasonLabels";

export type OrderViewV2OutletContext = {
  customerPublicId: string;
  customerOrderId: string;
};

export type Order = NonNullable<GraphQL.OrderDetailsQuery["orderByIdentifier"]>;
export type OrderPricing = NonNullable<Order["pricing"]>;
export type OrderRefund = NonNullable<Order["refunds"]>[number];
export type RefundDetails = NonNullable<OrderRefund["refundDetails"]>;

export type RefundBreakdown = {
  currency: GraphQL.SupportedCurrency;
  itemsTotal: number;
  shippingTotal: number;
  taxesTotal: number;
  refundTotal: number;
};

export const flexColumn = { display: "flex", flexDirection: "column" } as const;

export const linkStyle = {
  color: "inherit",
  "font-weight": tokens.font.weight.semibold,
} as const;

export function formatOrderDate(dateString: string): string {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} ${timePart}`;
}

export function isOrderPaid(order: Order): boolean {
  return (
    order.paymentStatus === GraphQL.PaymentStatus.Paid || order.paymentStatus === GraphQL.PaymentStatus.ExternallyPaid
  );
}

export function getPaymentStatusDisplayName(status: GraphQL.PaymentStatus | null | undefined): string {
  return status ? PAYMENT_STATUS_NAMES[status] : "";
}

export function getCurrencyLabel(currency: GraphQL.SupportedCurrency | null | undefined): string {
  return currency ?? "USD";
}

export function getMostRecentRefund(refunds: OrderRefund[]): OrderRefund | null {
  return refunds.reduce<OrderRefund | null>((latest, r) => {
    if (!latest?.createdAt) return r;
    if (!r.createdAt) return latest;
    return new Date(r.createdAt) > new Date(latest.createdAt) ? r : latest;
  }, null);
}

export function isFullRefund(refund: OrderRefund): boolean {
  return refund.refundDetails?.strategy === GraphQL.BraintreeRefundStrategy.OrderFullValue;
}

export function getRefundKindLabel(refund: OrderRefund): string {
  if (!refund.refundDetails) return "Refund";
  return isFullRefund(refund) ? "Full Refund" : "Partial Refund";
}

export function getRefundReasonDisplay(refund: OrderRefund): string | null {
  const reasonLabel = getRefundReasonLabel(refund.refundDetails?.reason);
  const note = refund.refundDetails?.reasonNote?.trim();
  if (reasonLabel && note) return `${reasonLabel} (${note})`;
  return reasonLabel ?? note ?? null;
}

// Returns null for legacy refunds created before the refund_details column existed.
export function toRefundBreakdown(
  refund: OrderRefund,
  currency: GraphQL.SupportedCurrency | null | undefined
): RefundBreakdown | null {
  const details = refund.refundDetails;
  if (!details) return null;
  return {
    currency: currency ?? GraphQL.SupportedCurrency.Usd,
    itemsTotal: details.itemsRefundAmount,
    shippingTotal: details.shippingRefundAmount,
    taxesTotal: details.taxesRefundAmount,
    refundTotal: refund.amount ?? 0,
  };
}
